/**
 * SiteMapPicker — Leaflet + OpenStreetMap site coordinate picker.
 *
 *   - Click anywhere on the map → drop a pin, fire `onChange({lat, lng})`
 *   - "Use my location" button → fills coords from `navigator.geolocation`
 *   - Renders a translucent circle showing the geofence radius so admins
 *     can eyeball whether 250m is too tight / too loose for the site
 *
 * No API key needed (OSM tiles are free for low-volume use). Defaults to
 * UK centroid when no coords are set yet.
 */
import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from './ui/button';
import { LocateFixed } from 'lucide-react';
import { toast } from 'sonner';

// Default Leaflet's marker icons break under Webpack — they expect static
// asset URLs that aren't bundled. Re-point them at the public CDN copies
// so pins render properly first time.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const UK_CENTRE = { lat: 54.0, lng: -2.5 };
const DEFAULT_ZOOM = 6;
const FOCUSED_ZOOM = 16;

const ClickHandler = ({ onChange }) => {
  useMapEvents({
    click(e) { onChange({ lat: e.latlng.lat, lng: e.latlng.lng }); },
  });
  return null;
};

/** Re-centres the map whenever lat/lng change externally (e.g. after the
 *  Use-my-location button or geocoding kicks in). Mounted once inside
 *  MapContainer so it has access to the leaflet map instance. */
const Recenter = ({ lat, lng }) => {
  const map = useMap();
  useEffect(() => {
    if (typeof lat === 'number' && typeof lng === 'number') {
      map.setView([lat, lng], FOCUSED_ZOOM);
    }
  }, [lat, lng, map]);
  return null;
};

export const SiteMapPicker = ({ lat, lng, radiusMeters = 250, onChange }) => {
  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported by this browser');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => onChange({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => toast.error(err.message || 'Could not read your location'),
      { enableHighAccuracy: true, timeout: 10_000 },
    );
  };

  const hasPin = typeof lat === 'number' && typeof lng === 'number';
  const centre = hasPin ? { lat, lng } : UK_CENTRE;

  return (
    <div className="space-y-2" data-testid="site-map-picker">
      <div className="h-56 w-full rounded-md overflow-hidden border border-slate-200 relative">
        <MapContainer
          center={[centre.lat, centre.lng]}
          zoom={hasPin ? FOCUSED_ZOOM : DEFAULT_ZOOM}
          scrollWheelZoom={false}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <ClickHandler onChange={onChange} />
          {hasPin && (
            <>
              <Recenter lat={lat} lng={lng} />
              <Marker position={[lat, lng]} />
              <Circle
                center={[lat, lng]}
                radius={Math.max(50, Number(radiusMeters) || 250)}
                pathOptions={{ color: '#2563eb', fillColor: '#3b82f6', fillOpacity: 0.12 }}
              />
            </>
          )}
        </MapContainer>
      </div>
      <div className="flex items-center justify-between text-xs">
        <div className="text-slate-500">
          {hasPin
            ? <>Tap the map to move the pin · radius {Math.max(50, Number(radiusMeters) || 250)}m shown in blue</>
            : <>Tap the map to drop a pin, or use your phone location ↓</>
          }
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={useMyLocation}
          data-testid="use-my-location-btn"
        >
          <LocateFixed className="h-3.5 w-3.5 mr-1.5" /> Use my location
        </Button>
      </div>
    </div>
  );
};
